import React from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";

interface LoadingOverlayProps {
  isLoading: boolean;
  operation?: "initial" | "pagination" | "sort" | "none";
  page?: number;
}

/**
 * LoadingOverlay component displays operation-specific loading messages
 * with an animated spinner centered on the screen
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isLoading,
  operation = "none",
  page,
}) => {
  const theme = useTheme();

  if (!isLoading || operation === "none") {
    return null;
  }

  const getMessage = (): string => {
    switch (operation) {
      case "initial":
        return "Searching YouTube...";
      case "pagination":
        return page !== undefined
          ? `Loading page ${page}...`
          : "Loading results...";
      case "sort":
        return "Sorting results...";
      default:
        return "Loading...";
    }
  };

  return (
    <Backdrop
      sx={{
        color: "#fff",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        backdropFilter: "blur(4px)",
        zIndex: theme.zIndex.modal + 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
      }}
      open={true}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <CircularProgress
          size={60}
          sx={{
            color: theme.palette.primary.main,
            mb: 2,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            color: "#fff",
            fontWeight: 500,
          }}
          aria-busy="true"
          aria-live="polite"
        >
          {getMessage()}
        </Typography>
      </Box>
    </Backdrop>
  );
};

export default LoadingOverlay;
